/**
 * Ground AOE Marker Renderer (L4-VIS-002)
 *
 * Renders persistent hex-shaped ground markers for AOE/breath/strike abilities.
 * Shows danger zones before damage is applied, improving tactical readability.
 *
 * Visual design:
 * - Outer ring: Thick (3px), element color, 80% alpha
 * - Inner ring: Thin (1px), darker shade, 40% alpha
 * - Fill: Subtle gradient, 15% alpha at center
 * - Animation: Subtle pulse (±5% scale)
 */

import * as PIXI from 'pixi.js';

export interface GroundMarkerConfig {
  /** Center X position in world coordinates */
  centerX: number;
  /** Center Y position in world coordinates */
  centerY: number;
  /** Radius in hex units (1 = single hex) */
  radiusHexes: number;
  /** Element color (e.g., 0xFF4400 for fire) */
  elementColor: number;
  /** Total visible duration in milliseconds */
  duration: number;
  /** Delay before showing marker (default: 0) */
  appearDelay?: number;
  /** Marker shape type */
  shape?: 'hex' | 'circle';
  /** Enable pulse animation */
  pulseAnimation?: boolean;
}

// Hex grid constants (should match your hex grid renderer)
const HEX_SIZE = 32; // Radius of a single hex in pixels
const HEX_WIDTH = HEX_SIZE * 2;
const HEX_HEIGHT = HEX_SIZE * Math.sqrt(3);

/**
 * Render a hex-aligned ground AOE marker
 *
 * @param container - PIXI container to add marker to (should be ground markers layer)
 * @param config - Marker configuration
 * @returns Cleanup function to remove marker early if needed
 */
export function renderHexGroundMarker(
  container: PIXI.Container,
  config: GroundMarkerConfig
): () => void {
  const shape = config.shape || 'hex';
  const enablePulse = config.pulseAnimation !== false;

  // Create marker graphics
  const marker = new PIXI.Graphics();
  marker.x = config.centerX;
  marker.y = config.centerY;
  marker.alpha = 0; // Start invisible
  marker.label = 'ground-marker';

  // Calculate radius in pixels
  const radiusPixels = config.radiusHexes * HEX_SIZE;

  // Draw the marker based on shape
  if (shape === 'hex') {
    drawHexMarker(marker, radiusPixels, config.elementColor);
  } else {
    drawCircleMarker(marker, radiusPixels, config.elementColor);
  }

  container.addChild(marker);

  // Animation state
  let animationFrameId: number | null = null;
  let isRemoved = false;

  // Animation timeline
  const fadeInDuration = 150;  // Fast fade in
  const fadeDuration = 300;     // Smooth fade out
  const startTime = performance.now() + (config.appearDelay || 0);

  const animate = () => {
    if (isRemoved) return;

    const elapsed = performance.now() - startTime;

    // Phase 1: Fade in
    if (elapsed < 0) {
      // Still in delay phase
      animationFrameId = requestAnimationFrame(animate);
      return;
    } else if (elapsed < fadeInDuration) {
      marker.alpha = elapsed / fadeInDuration;
    }
    // Phase 2: Hold (visible)
    else if (elapsed < config.duration - fadeDuration) {
      marker.alpha = 1;

      // Pulse animation during hold phase
      if (enablePulse) {
        const pulseTime = elapsed - fadeInDuration;
        const pulse = Math.sin(pulseTime * 0.003) * 0.05; // ±5% scale
        const newScale = 1 + pulse;
        marker.scale.x = newScale;
        marker.scale.y = newScale;
      }
    }
    // Phase 3: Fade out
    else if (elapsed < config.duration) {
      const fadeProgress = (elapsed - (config.duration - fadeDuration)) / fadeDuration;
      marker.alpha = 1 - fadeProgress;

      if (enablePulse) {
        const pulseTime = elapsed - fadeInDuration;
        const pulse = Math.sin(pulseTime * 0.003) * 0.05;
        const newScale = 1 + pulse;
        marker.scale.x = newScale;
        marker.scale.y = newScale;
      }
    }
    // Phase 4: Complete
    else {
      container.removeChild(marker);
      marker.destroy();
      isRemoved = true;
      return;
    }

    animationFrameId = requestAnimationFrame(animate);
  };

  // Start animation
  requestAnimationFrame(animate);

  // Return cleanup function
  return () => {
    if (!isRemoved) {
      isRemoved = true;
      if (animationFrameId !== null) {
        cancelAnimationFrame(animationFrameId);
      }
      if (marker.parent) {
        container.removeChild(marker);
      }
      marker.destroy();
    }
  };
}

/**
 * Draw hex-shaped marker with 3-part visual
 */
function drawHexMarker(
  graphics: PIXI.Graphics,
  radius: number,
  color: number
): void {
  const vertices = calculateHexVertices(radius);

  // Part 1: Fill (subtle gradient)
  graphics.poly(vertices);
  graphics.fill({ color, alpha: 0.15 });

  // Part 2: Inner ring (darker shade, thinner)
  const innerVertices = calculateHexVertices(radius * 0.9);
  const darkerColor = darken(color, 0.5);
  graphics.poly(innerVertices);
  graphics.stroke({ color: darkerColor, alpha: 0.4, width: 1 });

  // Part 3: Outer ring (bright, thick)
  graphics.poly(vertices);
  graphics.stroke({ color, alpha: 0.8, width: 3 });
}

/**
 * Draw circular marker with 3-part visual (fallback)
 */
function drawCircleMarker(
  graphics: PIXI.Graphics,
  radius: number,
  color: number
): void {
  // Part 1: Fill
  graphics.circle(0, 0, radius);
  graphics.fill({ color, alpha: 0.15 });

  // Part 2: Inner ring
  const darkerColor = darken(color, 0.5);
  graphics.circle(0, 0, radius * 0.9);
  graphics.stroke({ color: darkerColor, alpha: 0.4, width: 1 });

  // Part 3: Outer ring
  graphics.circle(0, 0, radius);
  graphics.stroke({ color, alpha: 0.8, width: 3 });
}

/**
 * Calculate hexagon vertices for flat-top orientation
 * Returns array of [x, y] points
 */
function calculateHexVertices(radius: number): number[] {
  const vertices: number[] = [];

  // Flat-top hexagon (6 vertices, starting from right)
  for (let i = 0; i < 6; i++) {
    const angleDeg = 60 * i;
    const angleRad = (Math.PI / 180) * angleDeg;
    const x = radius * Math.cos(angleRad);
    const y = radius * Math.sin(angleRad);
    vertices.push(x, y);
  }

  return vertices;
}

/**
 * Darken a color by a factor (0-1)
 */
function darken(color: number, factor: number): number {
  const r = (color >> 16) & 0xFF;
  const g = (color >> 8) & 0xFF;
  const b = color & 0xFF;

  const darkR = Math.floor(r * (1 - factor));
  const darkG = Math.floor(g * (1 - factor));
  const darkB = Math.floor(b * (1 - factor));

  return (darkR << 16) | (darkG << 8) | darkB;
}

/**
 * Render multiple ground markers at once
 * Useful for multi-target AOE abilities
 *
 * @param container - PIXI container
 * @param positions - Array of {x, y, radiusHexes} positions
 * @param config - Shared marker configuration
 * @returns Cleanup function for all markers
 */
export function renderMultipleGroundMarkers(
  container: PIXI.Container,
  positions: Array<{ x: number; y: number; radiusHexes: number }>,
  config: Omit<GroundMarkerConfig, 'centerX' | 'centerY' | 'radiusHexes'>
): () => void {
  const cleanupFunctions = positions.map(pos =>
    renderHexGroundMarker(container, {
      ...config,
      centerX: pos.x,
      centerY: pos.y,
      radiusHexes: pos.radiusHexes
    })
  );

  // Return combined cleanup function
  return () => {
    cleanupFunctions.forEach(cleanup => cleanup());
  };
}

/**
 * Calculate hex radius from AOE radius in pixels
 * Useful when converting from ability AOE data
 *
 * @param aoeRadiusPixels - AOE radius in pixels
 * @returns Radius in hex units
 */
export function pixelsToHexRadius(aoeRadiusPixels: number): number {
  return Math.max(1, Math.round(aoeRadiusPixels / HEX_SIZE));
}

/**
 * Preview helper: Render marker with immediate visibility (for testing)
 */
export function renderGroundMarkerPreview(
  container: PIXI.Container,
  config: Omit<GroundMarkerConfig, 'duration' | 'appearDelay'>
): () => void {
  return renderHexGroundMarker(container, {
    ...config,
    duration: 10000, // 10 second preview
    appearDelay: 0
  });
}
